import DiagramClass from "./DiagramClass.ts";
import DiagramRelationship from "@semantical/modules/diagram/DiagramRelationship.ts";
import { KnowledgeGraph, Individual, Relationship } from "@semantical/modules/knowledge-graph";
import { isIndividual } from "@semantical/modules/semantical-core";
import type { View } from "@semantical/modules/semantical-core";
import type { OntologyEnabledBoundedContext } from "@semantical/modules/workspace/OntologyEnabledBoundedContext";

export type RelationshipStyle = 's-curve' | 'arc';
export type AnchorMode = 'auto' | 'dynamic' | 'centered';
export type ViewportTransform = { x: number; y: number; k: number };

export default class Diagram implements View {
    id: string;
    name: string;
    /** The BoundedContext this diagram belongs to. Immutable once set. */
    boundedContextId: string;
    /** The dataset (KnowledgeGraph) this diagram is linked to. New elements are created in this dataset. */
    datasetId: string | null = null;
    classes: DiagramClass[] = [];
    relationships: DiagramRelationship[] = [];
    inferredRelationships: DiagramRelationship[] = [];  // Inferred by inference engine, not persisted
    relationshipStyle: RelationshipStyle = 'arc';  // Default to arc style for new diagrams
    anchorMode: AnchorMode = 'dynamic';  // Default to dynamic anchor mode for new diagrams
    forceLayoutEnabled: boolean = false;  // Whether force-directed layout is enabled
    viewportTransform: ViewportTransform = { x: 0, y: 0, k: 1 };  // Viewport pan/zoom state
    // Display options
    showStereotype: boolean = true;
    showEssentialProperties: boolean = true;
    showOptionalProperties: boolean = true;
    inferenceEnabled: boolean = false;
    editingMode: 'TBox' | 'ABox' = 'ABox';
    // Modeling Language - the ontology used for diagram visualization (diagram-level, not context-level)
    modelingLanguageIri: string | null = null;
    // Labeling Context - the SKOS vocabulary from the Ubiquitous Language used for labeling
    labelingContextIri: string | null = null;
    // Editor toolbar options
    showGrid: boolean = true;
    showToolbar: boolean = true;
    snapToGrid: boolean = true;
    // Visualization layers
    showDomainVisualization: boolean = false;

    constructor(name: string, boundedContextId: string, id?: string) {
        this.id = id ?? crypto.randomUUID();
        this.name = name;
        this.boundedContextId = boundedContextId;
    }

    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON() {
        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                classes: { '@id': 'hasClass', '@type': '@id', '@container': '@set' },
                relationships: { '@id': 'hasRelationship', '@type': '@id', '@container': '@set' }
            },
            '@type': 'Diagram',
            '@id': `urn:semantical:diagram:${this.id}`,
            id: this.id,
            name: this.name,
            boundedContextId: this.boundedContextId,
            datasetId: this.datasetId,
            classes: this.classes.map((aClass) => aClass.toJSON()),
            relationships: this.relationships.map((relationship) => relationship.toJSON()),
            relationshipStyle: this.relationshipStyle,
            anchorMode: this.anchorMode,
            forceLayoutEnabled: this.forceLayoutEnabled,
            viewportTransform: this.viewportTransform,
            showStereotype: this.showStereotype,
            showEssentialProperties: this.showEssentialProperties,
            showOptionalProperties: this.showOptionalProperties,
            inferenceEnabled: this.inferenceEnabled,
            editingMode: this.editingMode,
            modelingLanguageIri: this.modelingLanguageIri,
            labelingContextIri: this.labelingContextIri,
            showGrid: this.showGrid,
            showToolbar: this.showToolbar,
            snapToGrid: this.snapToGrid,
            showDomainVisualization: this.showDomainVisualization
        }
    }

    addClass(aClass: DiagramClass) {
        this.classes.push(aClass);
    }

    addRelationship(diagramRelationship: DiagramRelationship) {
        this.relationships.push(diagramRelationship);
    }

    removeClass(diagramClass: DiagramClass) {
        this.classes = this.classes.filter(c => c !== diagramClass);
    }

    removeRelationship(diagramRelationship: DiagramRelationship) {
        this.relationships = this.relationships.filter(r => r !== diagramRelationship);
    }

    /**
     * Set the relationship style.
     * Fixed anchor positions are preserved - Arc mode simply ignores them
     * while S-Curve mode uses them if set.
     */
    setRelationshipStyle(style: RelationshipStyle) {
        this.relationshipStyle = style;
        // Note: We no longer reset anchors here. Arc mode ignores fixed positions
        // but preserves them so they're restored when switching back to S-Curve.
    }

    /**
     * Set the anchor positioning mode and reset all fixed anchors.
     * When switching modes, anchors need to be recomputed for the new algorithm.
     */
    setAnchorMode(mode: AnchorMode) {
        this.anchorMode = mode;
        // Reset all fixed anchors so they get recomputed for the new mode
        for (const rel of this.relationships) {
            rel.resetAnchors();
        }
    }

    /**
     * Resolve any placeholder SchemaIndividuals from the bounded context.
     * Call this after ontologies are loaded to update diagram classes with full data.
     * Returns the number of placeholders that were resolved.
     */
    resolvePlaceholders(boundedContext: OntologyEnabledBoundedContext): number {
        let resolved = 0;
        for (const diagramClass of this.classes) {
            if (diagramClass.resolveFromBoundedContext(boundedContext)) {
                resolved++;
            }
        }
        return resolved;
    }

    /**
     * Set the viewport transform (pan/zoom state).
     */
    setViewportTransform(transform: ViewportTransform) {
        this.viewportTransform = transform;
    }

    /**
     * Set the modeling language (ontology IRI) for this diagram.
     * This is diagram-level configuration that does not affect the BoundedContext's semantic layer.
     */
    setModelingLanguage(iri: string | null): void {
        this.modelingLanguageIri = iri;
    }

    /**
     * Set the labeling context (SKOS vocabulary IRI) for this diagram.
     * Must be a vocabulary from the BoundedContext's Ubiquitous Language.
     */
    setLabelingContext(iri: string | null): void {
        this.labelingContextIri = iri;
    }

    getRelationshipsForClass(diagramClass: DiagramClass): DiagramRelationship[] {
        return this.relationships.filter(r => r.source === diagramClass || r.target === diagramClass);
    }

    /**
     * Get all relationships including inferred ones.
     */
    getAllRelationships(): DiagramRelationship[] {
        return [...this.relationships, ...this.inferredRelationships];
    }

    /**
     * Set the inferred relationships (replaces any existing).
     * Called by the inference engine when inference is enabled.
     */
    setInferredRelationships(inferred: DiagramRelationship[]): void {
        this.inferredRelationships = inferred;
    }

    /**
     * Clear all inferred relationships.
     * Called when inference is disabled.
     */
    clearInferredRelationships(): void {
        this.inferredRelationships = [];
    }

    /**
     * Get visible (non-hidden) classes.
     */
    getVisibleClasses(): DiagramClass[] {
        return this.classes.filter(c => !c.hidden);
    }

    /**
     * Get hidden classes.
     */
    getHiddenClasses(): DiagramClass[] {
        return this.classes.filter(c => c.hidden);
    }

    /**
     * Check if any classes are hidden.
     */
    hasHiddenClasses(): boolean {
        return this.classes.some(c => c.hidden);
    }

    /**
     * Unhide all hidden classes.
     */
    unhideAll(): void {
        this.classes.forEach(c => c.hidden = false);
    }

    /**
     * Get visible relationships (where both source and target are visible).
     */
    getVisibleRelationships(): DiagramRelationship[] {
        return this.relationships.filter(r => !r.source.hidden && !r.target.hidden);
    }

    /**
     * Get all visible relationships including inferred ones.
     */
    getAllVisibleRelationships(): DiagramRelationship[] {
        const visibleRegular = this.relationships.filter(r => !r.source.hidden && !r.target.hidden);
        const visibleInferred = this.inferredRelationships.filter(r => !r.source.hidden && !r.target.hidden);
        return [...visibleRegular, ...visibleInferred];
    }

    /**
     * Export visible diagram elements to Turtle format.
     * Includes only non-hidden individuals and ALL their assertions from the KnowledgeGraph.
     * The diagram filters individuals, not relationships - if an individual is visible,
     * all its object properties and data properties are exported.
     *
     * @param knowledgeGraph - The knowledge graph containing individuals and relationships
     * @param workspacePrefixes - Optional prefix map from Workspace.getPrefixes() (namespace → prefix)
     */
    toTurtle(knowledgeGraph: KnowledgeGraph, workspacePrefixes?: Map<string, string>): string {
        // Start with standard W3C prefixes (always needed)
        const knownPrefixes: Record<string, string> = {
            'http://www.w3.org/1999/02/22-rdf-syntax-ns#': 'rdf',
            'http://www.w3.org/2000/01/rdf-schema#': 'rdfs',
            'http://www.w3.org/2002/07/owl#': 'owl',
            'http://www.w3.org/2001/XMLSchema#': 'xsd',
        };

        // Add prefixes from workspace (all loaded ontologies)
        if (workspacePrefixes) {
            for (const [namespace, prefix] of workspacePrefixes) {
                knownPrefixes[namespace] = prefix;
            }
        }

        // Add the KG's own URI as the default namespace prefix
        // Ensure it ends with # or / for proper prefix usage
        const kgNamespace = knowledgeGraph.uri.endsWith('#') || knowledgeGraph.uri.endsWith('/')
            ? knowledgeGraph.uri
            : knowledgeGraph.uri + '#';
        knownPrefixes[kgNamespace] = '';

        // Collect which prefixes are actually used
        const usedPrefixes = new Set<string>(['rdf', 'rdfs', 'owl']);

        // Helper to shorten a URI using known prefixes
        const formatUri = (uri: string): string => {
            for (const [ns, prefix] of Object.entries(knownPrefixes)) {
                if (uri.startsWith(ns)) {
                    usedPrefixes.add(prefix);
                    return `${prefix}:${uri.substring(ns.length)}`;
                }
            }
            return `<${uri}>`;
        };

        // Helper to escape string literals
        const escapeString = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

        // Get visible elements
        const visibleClasses = this.getVisibleClasses();

        // Get ALL relationships from KnowledgeGraph where subject is visible on diagram
        const visibleUris = new Set(visibleClasses.map(dc => dc.modelElement.uri));
        const visibleRelationships = Array.from(knowledgeGraph.relationships.values()).filter(
            rel => visibleUris.has(rel.subject.uri)
        );

        // Build a map of relationships by subject URI for grouping
        const relsBySubject = new Map<string, Relationship[]>();
        for (const rel of visibleRelationships) {
            const subjectUri = rel.subject.uri;
            if (!relsBySubject.has(subjectUri)) {
                relsBySubject.set(subjectUri, []);
            }
            relsBySubject.get(subjectUri)!.push(rel);
        }

        // Content lines (will prepend prefixes later)
        const contentLines: string[] = [];

        // Header comment
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push(`# Visible Diagram Elements: ${this.name}`);
        contentLines.push(`# Exported: ${new Date().toISOString()}`);
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');

        // Ontology declaration with imports (from KnowledgeGraph)
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('# Ontology Declaration');
        contentLines.push('# -----------------------------------------------------------');
        contentLines.push('');

        // Build ontology declaration using KG's URI and imports
        const kgOntologyUri = knowledgeGraph.uri.endsWith('#') || knowledgeGraph.uri.endsWith('/')
            ? knowledgeGraph.uri.replace(/[#/]$/, '')
            : knowledgeGraph.uri;

        if (knowledgeGraph.imports.size === 0) {
            contentLines.push(`<${kgOntologyUri}> a owl:Ontology .`);
        } else {
            contentLines.push(`<${kgOntologyUri}> a owl:Ontology ;`);

            const importEntries = Array.from(knowledgeGraph.imports.entries());
            importEntries.forEach(([ontologyIri, versionIri], index) => {
                // Use versionIRI if available, otherwise ontologyIRI
                const importIri = versionIri || ontologyIri;
                const isLast = index === importEntries.length - 1;
                contentLines.push(`    owl:imports <${importIri}>${isLast ? ' .' : ' ;'}`);
            });
        }
        contentLines.push('');

        // Export visible individuals/classes
        if (visibleClasses.length > 0) {
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('# Individuals');
            contentLines.push('# -----------------------------------------------------------');
            contentLines.push('');

            for (const diagramClass of visibleClasses) {
                const modelElement = diagramClass.modelElement;
                const props: string[] = [];

                // Types (owl:NamedIndividual and the class if available)
                if (modelElement instanceof Individual) {
                    // ABox Individual - has modelClass reference
                    props.push(`    a owl:NamedIndividual ,`);
                    props.push(`      ${formatUri(modelElement.modelClass.uri)} ;`);
                } else if (isIndividual(modelElement)) {
                    // SchemaIndividual (TBox individual) - has classUris array
                    const classUri = modelElement.classUris[0];
                    if (classUri) {
                        props.push(`    a owl:NamedIndividual ,`);
                        props.push(`      ${formatUri(classUri)} ;`);
                    } else {
                        props.push(`    a owl:NamedIndividual ;`);
                    }
                } else {
                    props.push(`    a owl:Class ;`);
                }

                // Label
                props.push(`    rdfs:label "${escapeString(modelElement.name)}"`);

                // Definition if available
                if (modelElement.definition) {
                    props[props.length - 1] += ' ;';
                    props.push(`    rdfs:comment "${escapeString(modelElement.definition)}"`);
                }

                // Data property values for individuals (supports multi-value)
                if (modelElement instanceof Individual) {
                    modelElement.propertyValues.forEach((values, propertyUri) => {
                        // Find the property definition to get the range/datatype
                        const dataProp = modelElement.modelClass.dataProperties?.find(p => p.uri === propertyUri);
                        const range = dataProp?.range?.toLowerCase() || 'string';

                        // Format each value based on datatype
                        for (const value of values) {
                            props[props.length - 1] += ' ;';

                            let formattedValue: string;
                            if (range === 'date' || range === 'datetime') {
                                formattedValue = `"${escapeString(value)}"^^xsd:date`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            } else if (range === 'integer' || range === 'int' || range === 'long' || range === 'short') {
                                formattedValue = `"${escapeString(value)}"^^xsd:integer`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            } else if (range === 'decimal' || range === 'float' || range === 'double') {
                                formattedValue = `"${escapeString(value)}"^^xsd:decimal`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            } else if (range === 'boolean') {
                                formattedValue = `"${value === 'true' ? 'true' : 'false'}"^^xsd:boolean`;
                                usedPrefixes.add('xsd');
                                if (!knownPrefixes['http://www.w3.org/2001/XMLSchema#']) {
                                    knownPrefixes['http://www.w3.org/2001/XMLSchema#'] = 'xsd';
                                }
                            } else {
                                // Default to string
                                formattedValue = `"${escapeString(value)}"`;
                            }

                            props.push(`    ${formatUri(propertyUri)} ${formattedValue}`);
                        }
                    });
                }

                // Relationships where this element is the subject (from DomainModel)
                const rels = relsBySubject.get(modelElement.uri) || [];
                for (const rel of rels) {
                    props[props.length - 1] += ' ;';
                    const predicateUri = rel.predicate.uri;
                    const objectUri = rel.object.uri;
                    props.push(`    ${formatUri(predicateUri)} ${formatUri(objectUri)}`);
                }

                contentLines.push(formatUri(modelElement.uri));
                props[props.length - 1] += ' .';
                contentLines.push(...props);
                contentLines.push('');
            }
        }

        // Build prefix declarations (only for used prefixes)
        const prefixLines: string[] = [];
        const nsToPrefix = Object.entries(knownPrefixes);
        // Sort by prefix name for consistent output
        nsToPrefix.sort((a, b) => a[1].localeCompare(b[1]));

        for (const [ns, prefix] of nsToPrefix) {
            if (usedPrefixes.has(prefix)) {
                // Pad prefix for alignment
                const paddedPrefix = (prefix + ':').padEnd(10);
                prefixLines.push(`@prefix ${paddedPrefix}<${ns}> .`);
            }
        }
        prefixLines.push('');

        return [...prefixLines, ...contentLines].join('\n');
    }

    static fromJSON(json: any, knowledgeGraph: KnowledgeGraph, boundedContext?: OntologyEnabledBoundedContext): Diagram {
        // boundedContextId is required - use from JSON or fall back to a placeholder that must be set later
        const boundedContextId = json.boundedContextId ?? '__unassigned__';
        const diagram = new Diagram(json.name, boundedContextId, json.id);

        // Restore datasetId if present
        if (json.datasetId) {
            diagram.datasetId = json.datasetId;
        }

        // Restore relationship style if present
        if (json.relationshipStyle === 's-curve' || json.relationshipStyle === 'arc') {
            diagram.relationshipStyle = json.relationshipStyle;
        }

        // Restore anchor mode if present
        if (json.anchorMode === 'auto' || json.anchorMode === 'dynamic' || json.anchorMode === 'centered') {
            diagram.anchorMode = json.anchorMode;
        }

        // Restore force layout enabled if present
        if (typeof json.forceLayoutEnabled === 'boolean') {
            diagram.forceLayoutEnabled = json.forceLayoutEnabled;
        }

        // Restore viewport transform if present
        if (json.viewportTransform && typeof json.viewportTransform.x === 'number' && typeof json.viewportTransform.y === 'number' && typeof json.viewportTransform.k === 'number') {
            diagram.viewportTransform = json.viewportTransform;
        }

        // Restore display options if present
        if (typeof json.showStereotype === 'boolean') {
            diagram.showStereotype = json.showStereotype;
        }
        if (typeof json.showEssentialProperties === 'boolean') {
            diagram.showEssentialProperties = json.showEssentialProperties;
        }
        if (typeof json.showOptionalProperties === 'boolean') {
            diagram.showOptionalProperties = json.showOptionalProperties;
        }
        if (typeof json.inferenceEnabled === 'boolean') {
            diagram.inferenceEnabled = json.inferenceEnabled;
        }
        if (json.editingMode === 'TBox' || json.editingMode === 'ABox') {
            diagram.editingMode = json.editingMode;
        }
        if (typeof json.modelingLanguageIri === 'string' || json.modelingLanguageIri === null) {
            diagram.modelingLanguageIri = json.modelingLanguageIri;
        }
        if (typeof json.labelingContextIri === 'string' || json.labelingContextIri === null) {
            diagram.labelingContextIri = json.labelingContextIri;
        }
        if (typeof json.showGrid === 'boolean') {
            diagram.showGrid = json.showGrid;
        }
        if (typeof json.showToolbar === 'boolean') {
            diagram.showToolbar = json.showToolbar;
        }
        if (typeof json.snapToGrid === 'boolean') {
            diagram.snapToGrid = json.snapToGrid;
        }
        if (typeof json.showDomainVisualization === 'boolean') {
            diagram.showDomainVisualization = json.showDomainVisualization;
        }

        // Build a map of element URI -> DiagramClass for relationship linking
        // Key by modelElement.uri (concept.uri for SchemaClass, individualUri for Individual)
        const diagramClassMap = new Map<string, DiagramClass>();

        // First pass: restore classes
        for (const classJson of json.classes) {
            const diagramClass = DiagramClass.fromJSON(classJson, knowledgeGraph, boundedContext);
            if (diagramClass) {
                diagram.addClass(diagramClass);
                // Key by the modelElement's uri (concept.uri for SchemaClass, individualUri for Individual)
                diagramClassMap.set(diagramClass.modelElement.uri, diagramClass);
            }
        }

        // Second pass: restore relationships (need classes to be available)
        for (const relJson of json.relationships) {
            const diagramRel = DiagramRelationship.fromJSON(relJson, diagramClassMap, knowledgeGraph);
            if (diagramRel) {
                diagram.addRelationship(diagramRel);
            }
        }

        return diagram;
    }
}